import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Globe, Heart } from 'lucide-react';
import './SupportedCharitiesModal.css';

const charityData = [
  // 🌍 For People Worldwide
  {
    category: 'worldwide',
    categoryTitle: 'For People Worldwide',
    categoryIcon: <Globe size={24} />,
    charities: [
      {
        name: "Save the Children",
        description: "Save the Children works in over 100 countries to give children a healthy start in life, the opportunity to learn, and protection from harm. They respond to emergencies while working to address root causes of challenges facing children.",
        focus: "Child protection, education, health care, emergency response, and advocacy for children's rights in both crisis and development contexts.",
        website: "https://www.savethechildren.org",
        address: "0x17df6ede80e8c3338cf0aa59f7d7063c419629b1"
      },
      {
        name: "International Rescue Committee",
        description: "The IRC provides emergency aid and long-term assistance to refugees and displaced persons. They focus on helping people whose lives have been shattered by conflict and disaster to survive, recover, and rebuild their lives.",
        focus: "Emergency response, health care, education, economic wellbeing, and empowerment programs for refugees, particularly women and children.",
        website: "https://www.rescue.org",
        address: "0x566048cec786da7207502c3365e356f3fb013375"
      },
      {
        name: "Helen Keller International",
        description: "HKI combats the causes and consequences of blindness and malnutrition by establishing programs based on evidence and research in vision, health, and nutrition. They work to create lasting change in communities worldwide.",
        focus: "Preventing blindness and malnutrition through vitamin A supplementation, nutrition programs, and eye care services in developing countries.",
        website: "https://www.hki.org",
        address: "0x017a97e17d0effbfd2d0cc07698c8aa678efb330"
      }
    ]
  },
  // 🌱 For Environmental Protection
  {
    category: 'environment',
    categoryTitle: 'For Environmental Protection',
    categoryIcon: <span style={{fontSize: '24px'}}>🌱</span>,
    charities: [
      {
        name: "Rainforest Alliance",
        description: "The Rainforest Alliance works to conserve biodiversity and ensure sustainable livelihoods by transforming land-use practices, business practices, and consumer behavior.",
        focus: "Sustainable agriculture, forest conservation, climate adaptation, and supporting farmers and forest communities with certification programs and training.",
        website: "https://www.rainforest-alliance.org",
        address: "0x68d91683233a2ec74b5d6098b25a2c1b120d5f3d"
      },
      {
        name: "350.org",
        description: "350.org is building a global climate movement. They organize campaigns, support grassroots climate activism, and work to keep fossil fuels in the ground and accelerate the transition to renewable energy.",
        focus: "Climate activism, fossil fuel divestment campaigns, renewable energy advocacy, and supporting local climate movements worldwide.",
        website: "https://350.org",
        address: "0x2cdb6c3fa845617f13b0fb3679872a9eadf1871c"
      },
      {
        name: "Earthwatch Institute",
        description: "Earthwatch engages people worldwide in scientific field research and education to promote understanding and action necessary for a sustainable environment.",
        focus: "Citizen science projects, biodiversity research, climate change studies, and training the next generation of environmental scientists.",
        website: "https://earthwatch.org",
        address: "0xdb9729f7efb51748ef5033a28552356b3f8a8473"
      }
    ]
  },
  // 🐾 For Animal Protection
  {
    category: 'animals',
    categoryTitle: 'For Animal Protection',
    categoryIcon: <span style={{fontSize: '24px'}}>🐾</span>,
    charities: [
      {
        name: "World Animal Protection",
        description: "World Animal Protection moves the world to protect animals. They work to improve the lives of animals by influencing policy, changing industry practices, and building global awareness.",
        focus: "Ending factory farming, protecting wildlife, disaster relief for animals, ending cruel practices like bear bile farming and live animal transport.",
        website: "https://www.worldanimalprotection.org",
        address: "0x00e7588041ac592f8560d78cd36e5e22daf8b4ed"
      },
      {
        name: "Humane Society International",
        description: "HSI works around the globe to promote the human-animal bond, rescue and protect dogs and cats, improve farm animal welfare, protect wildlife, promote animal-free testing, and respond to natural disasters.",
        focus: "Animal testing alternatives, factory farming reform, wildlife protection, companion animal welfare, and emergency animal rescue operations.",
        website: "https://www.hsi.org",
        address: "0x9ae9f574913313e19bdeedd1fcab56be455b505c"
      },
      {
        name: "International Fund for Animal Welfare",
        description: "IFAW rescues individual animals, safeguards populations, preserves habitat, and advocates for greater protections. They work both locally and globally to help animals and people thrive together.",
        focus: "Wildlife rescue and rehabilitation, habitat conservation, anti-poaching efforts, marine mammal protection, and community-based conservation programs.",
        website: "https://www.ifaw.org",
        address: "0x1a0bd6561ce4a81556f8c997845e5d2dae907a89"
      }
    ]
  }
];

const SupportedCharitiesModal = ({ isOpen, onClose }) => {
  const [expandedAddresses, setExpandedAddresses] = React.useState(new Set());

  if (!isOpen) return null;

  const toggleAddress = (address) => {
    const newExpanded = new Set(expandedAddresses);
    if (newExpanded.has(address)) {
      newExpanded.delete(address);
    } else {
      newExpanded.add(address);
    }
    setExpandedAddresses(newExpanded);
  };

  const formatAddress = (address) => {
    if (expandedAddresses.has(address)) {
      return address;
    }
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'worldwide': return '#3b82f6'; // Blue
      case 'environment': return '#10b981'; // Green
      case 'animals': return '#f59e0b'; // Orange
      default: return '#6366f1';
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="charities-modal"
          initial={{ opacity: 0, scale: 0.9, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 50 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>💝 Supported Charities</h2>
            <button
              className="close-button"
              onClick={onClose}
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
          </div>

          <div className="modal-content">
            <div className="intro-section">
              <p>
                Every confession created on GuiltBox supports <strong>9 carefully selected charities</strong> working 
                to make the world a better place. When you create a confession, you choose 3 organizations 
                that resonate with your values, and donations are distributed equally among them and the platform.
              </p>
            </div>

            <div className="categories-container">
              {charityData.map((categoryData, categoryIndex) => (
                <motion.div
                  key={categoryData.category}
                  className="category-section"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
                >
                  <div 
                    className="category-header"
                    style={{ 
                      background: `linear-gradient(135deg, ${getCategoryColor(categoryData.category)}15, ${getCategoryColor(categoryData.category)}25)`,
                      borderColor: `${getCategoryColor(categoryData.category)}40`
                    }}
                  >
                    <div className="category-icon" style={{ color: getCategoryColor(categoryData.category) }}>
                      {categoryData.categoryIcon}
                    </div>
                    <h3>{categoryData.categoryTitle}</h3>
                  </div>

                  <div className="charities-grid">
                    {categoryData.charities.map((charity, charityIndex) => (
                      <motion.div
                        key={charity.name}
                        className="charity-card"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: (categoryIndex * 0.1) + (charityIndex * 0.05) }}
                        whileHover={{ y: -5, transition: { duration: 0.2 } }}
                      >
                        <div className="charity-header">
                          <h4>{charity.name}</h4>
                          <a
                            href={charity.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="website-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink size={16} />
                          </a>
                        </div>
                        
                        <div className="charity-content">
                          <p className="charity-description">{charity.description}</p>
                          
                          <div className="wallet-section">
                            <h5>Wallet Address:</h5>
                            <button
                              className="wallet-address"
                              onClick={() => toggleAddress(charity.address)}
                              title="Click to toggle full address"
                            >
                              {formatAddress(charity.address)}
                            </button>
                          </div>
                          
                          <div className="focus-section">
                            <h5>Key Focus Areas:</h5>
                            <p className="focus-text">{charity.focus}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="footer-section">
              <p className="footer-text">
                When you create a confession, choose 3 organizations that align with your values. 
                Every token purchase distributes 25% to each selected charity and 25% to platform sustainability.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SupportedCharitiesModal;